import { useRef, useState, useEffect } from "react";
import { Users, ArrowLeftRight } from "lucide-react";
import { DAY_W, LEFT_W, SLANT, barColor, barFor, dayNum, weekdayShortStr, isWeekendStr, addDaysStr } from "@/lib/calendarUtils";

export function CalendarGrid({ start, numDays, days, properties, bars, totals, presenceRows, todayIso, onSelect, onReschedule, isAdmin }) {
  const gridW = LEFT_W + numDays * DAY_W;
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const lpRef = useRef(null);
  const LONG_MS = 450, MOVE_CANCEL = 12;

  // Su touch, blocca lo scroll SOLO quando il drag è armato (long-press attivo).
  // Il listener deve essere non-passivo perché preventDefault funzioni.
  useEffect(() => {
    const preventScroll = (e) => {
      if (dragRef.current && dragRef.current.armed && dragRef.current.isTouch) {
        e.preventDefault();
      }
    };
    document.addEventListener("touchmove", preventScroll, { passive: false });
    return () => document.removeEventListener("touchmove", preventScroll, { passive: false });
  }, []);

  const clearLP = () => { if (lpRef.current) { clearTimeout(lpRef.current); lpRef.current = null; } };

  const beginDrag = (e, x) => {
    const canDrag = !!onReschedule && (isAdmin || x.booking.own) && (x.booking.segments || []).length < 2;
    const isTouch = e.pointerType === "touch";
    dragRef.current = { key: x.key, x, startX: e.clientX, startY: e.clientY, moved: false, dragged: false, armed: false, canDrag, isTouch, el: e.currentTarget, pointerId: e.pointerId };
    if (canDrag && !isTouch) {
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
      dragRef.current.armed = true;
      setDrag({ key: x.key, dx: 0, dy: 0 });
    } else if (canDrag && isTouch) {
      clearLP();
      lpRef.current = setTimeout(() => {
        const d = dragRef.current;
        if (!d || d.moved) return;
        try { d.el.setPointerCapture(d.pointerId); } catch (_) {}
        d.el.style.touchAction = "none";
        d.armed = true;
        setDrag({ key: d.key, dx: 0, dy: 0 });
        if (navigator.vibrate) navigator.vibrate(15);
      }, LONG_MS);
    }
  };
  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    if (!d.armed) {
      if (Math.abs(dx) > MOVE_CANCEL || Math.abs(dy) > MOVE_CANCEL) { clearLP(); d.moved = true; }
      return;
    }
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) d.dragged = true;
    setDrag({ key: d.key, dx, dy });
  };
  const endDrag = (e, x) => {
    const d = dragRef.current;
    clearLP();
    dragRef.current = null;
    setDrag(null);
    if (d && d.el) d.el.style.touchAction = "";
    if (!d) return;
    if (!d.armed) {
      if (!d.moved) onSelect({ ...x.booking, seg_property_id: x.property_id, seg_checkin: x.checkin, seg_checkout: x.checkout });
      return;
    }
    if (!d.dragged) {
      onSelect({ ...x.booking, seg_property_id: x.property_id, seg_checkin: x.checkin, seg_checkout: x.checkout });
      return;
    }
    const dx = e.clientX - d.startX;
    if (d.el) d.el.style.pointerEvents = "none";
    const under = document.elementFromPoint(e.clientX, e.clientY);
    if (d.el) d.el.style.pointerEvents = "";
    const roomEl = under && under.closest("[data-room-id]");
    const toRoom = (roomEl && roomEl.getAttribute("data-room-id")) || x.property_id;
    const deltaDays = Math.round(dx / DAY_W);
    if (toRoom === x.property_id && deltaDays === 0) return;
    const nc = addDaysStr(x.checkin, deltaDays);
    const co = addDaysStr(x.checkout, deltaDays);
    onReschedule(x.booking, toRoom, nc, co);
  };
  const cancelDrag = () => { clearLP(); const d = dragRef.current; if (d && d.el) d.el.style.touchAction = ""; dragRef.current = null; setDrag(null); };

  return (
    <div className="cal-scroll overflow-auto max-h-[75vh] border border-slate-200 rounded-md bg-white">
      <div style={{ minWidth: gridW }}>
        {/* header */}
        <div className="flex border-b border-slate-200 bg-slate-50 sticky top-0 z-20">
          <div
            className="sticky left-0 z-30 bg-slate-50 border-r border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 flex items-center"
            style={{ width: LEFT_W }}
          >
            Camere
          </div>
          {days.map((d) => {
            const isWknd = isWeekendStr(d);
            const isToday = d === todayIso;
            return (
              <div
                key={d}
                className={`text-center py-1.5 border-r border-slate-100 ${
                  isToday ? "bg-sky-100" : isWknd ? "bg-slate-100" : ""
                }`}
                style={{ width: DAY_W }}
              >
                <div className="text-sm font-bold text-slate-800 leading-none">{dayNum(d)}</div>
                <div className="text-[10px] text-slate-400 uppercase">
                  {weekdayShortStr(d)}
                </div>
              </div>
            );
          })}
        </div>

        {/* rooms */}
        {properties.map((p, idx) => {
          const showHeader = idx === 0 || properties[idx - 1].structure_name !== p.structure_name;
          return (
            <div key={p.id}>
              {showHeader && (
                <div className="flex bg-slate-100 border-b border-slate-200" style={{ height: 26 }}>
                  <div className="sticky left-0 z-10 bg-slate-100 px-3 flex items-center text-[11px] font-bold uppercase tracking-wide text-slate-500" style={{ width: LEFT_W }}>
                    {p.structure_name}
                  </div>
                  <div className="bg-slate-100" style={{ width: numDays * DAY_W }} />
                </div>
              )}
              <div className="flex border-b border-slate-100 relative" style={{ height: 44 }}>
                <div
                  className="sticky left-0 z-10 bg-white border-r border-slate-200 px-2 sm:px-3 flex items-center text-[11px] sm:text-xs font-semibold text-slate-700 leading-tight"
                  style={{ width: LEFT_W }}
                >
                  {p.name}
                </div>
                <div className="relative flex-1" style={{ width: numDays * DAY_W }} data-room-id={p.id}>
                  {days.map((d, i) => {
                    const isWknd = isWeekendStr(d);
                    const isToday = d === todayIso;
                    return (
                      <div
                        key={d}
                        className={`absolute top-0 bottom-0 border-r border-slate-100 ${
                          isToday ? "bg-sky-50" : isWknd ? "bg-slate-50" : ""
                        }`}
                        style={{ left: i * DAY_W, width: DAY_W }}
                      />
                    );
                  })}
                  {bars
                    .filter((x) => x.property_id === p.id)
                    .map((x) => {
                      const b = x.booking;
                      const pos = barFor(x, start, numDays);
                      if (!pos) return null;
                      const isPast = x.checkout < todayIso;
                      const dragging = drag && drag.key === x.key;
                      return (
                        <button
                          key={x.key}
                          data-testid={`cal-bar-${b.id}`}
                          onPointerDown={(e) => beginDrag(e, x)}
                          onPointerMove={onDragMove}
                          onPointerUp={(e) => endDrag(e, x)}
                          onPointerCancel={cancelDrag}
                          onContextMenu={(e) => { if (dragRef.current && dragRef.current.isTouch) e.preventDefault(); }}
                          className={`absolute top-1 h-9 ${barColor(b, isPast)} text-white flex flex-col justify-center shadow-sm transition-[filter] duration-150 overflow-hidden select-none ${dragging ? "opacity-80 z-50 cursor-grabbing shadow-lg" : "cursor-grab hover:brightness-95"}`}
                          style={{ left: pos.left, width: pos.width, clipPath: `polygon(${SLANT}px 0, 100% 0, calc(100% - ${SLANT}px) 100%, 0 100%)`, transform: dragging ? `translate(${drag.dx}px, ${drag.dy}px)` : undefined }}
                          title={`${b.guest_surname} · ${b.origine} · ${x.checkin} → ${x.checkout}`}
                        >
                          <span className="truncate text-[11px] font-semibold leading-tight flex items-center gap-1 px-2.5 pointer-events-none">
                            {b.segments && b.segments.length > 1 && <ArrowLeftRight size={9} className="opacity-90 shrink-0" />}
                            {b.guest_surname}
                            <span className="opacity-90 whitespace-nowrap flex items-center gap-0.5"><Users size={9} /> {(b.adults || 0) + (b.children || 0)}</span>
                          </span>
                          <span className="truncate text-[9px] opacity-90 leading-tight px-2.5 pointer-events-none">{b.origine}</span>
                        </button>
                      );
                    })}
                </div>
              </div>
            </div>
          );
        })}

        {/* totals */}
        {[
          ["Arrivi", "arrivi", "text-sky-700"],
          ["Partenze", "partenze", "text-amber-600"],
        ].map(([label, key, color]) => (
          <div key={key} className="flex border-b border-slate-100 bg-slate-50/60" style={{ height: 30 }}>
            <div
              className="sticky left-0 z-10 bg-slate-50 border-r border-slate-200 px-3 flex items-center text-xs font-bold text-slate-600"
              style={{ width: LEFT_W }}
            >
              {label}
            </div>
            {totals.map((t, i) => (
              <div
                key={days[i] || i}
                className={`text-center text-xs font-semibold flex items-center justify-center border-r border-slate-100 ${color}`}
                style={{ width: DAY_W }}
              >
                {t[key] || ""}
              </div>
            ))}
          </div>
        ))}

        {/* presenze in-house divise per guesthouse */}
        {(presenceRows || []).map((row) => (
          <div key={row.label} data-testid={`presence-row-${row.structure_id}`} className="flex border-b border-slate-100 bg-emerald-50/40" style={{ height: 30 }}>
            <div
              className="sticky left-0 z-10 bg-emerald-50/60 border-r border-slate-200 px-3 flex items-center gap-1.5 text-xs font-bold text-emerald-700 truncate"
              style={{ width: LEFT_W }}
              title={`Presenze · ${row.label}`}
            >
              <Users size={12} className="shrink-0" /> {row.label}
            </div>
            {row.values.map((v, i) => (
              <div
                key={days[i] || i}
                className="text-center text-xs font-semibold flex items-center justify-center border-r border-slate-100 text-emerald-700"
                style={{ width: DAY_W }}
              >
                {v || ""}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
